// src/pages/profile-settings/patient/Languages.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, CircleX as CircleXIcon, Plus } from "lucide-react";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Card from "@/components/ui/Card.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Select from "@/components/ui/Select";
import TapButton from "@/pages/onboarding/_shared/TapButton.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { loadLanguagesFromCSVs } from "@/utils/languages.js";
import { shouldHideUntranslatedCatalog } from "@/utils/i18nGuardrails.js";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

const CSV_PATHS = [
  "/data/language_use_angola.csv",
  "/data/language_use_botswana.csv",
  "/data/language_use_mozambique.csv",
  "/data/language_use_namibia.csv",
  "/data/language_use_southafrica.csv",
  "/data/language_use_zambia.csv",
  "/data/language_use_zimbabwe.csv",
];

const FRENCH_LANGUAGE_LABELS = {
  English: "Anglais",
  French: "Français",
  Tswana: "Setswana",
  Setswana: "Setswana",
  "Zimbabwean Ndebele": "Ndebele du Zimbabwe",
  Ndebele: "Ndebele",
  Portuguese: "Portugais",
  German: "Allemand",
};

function isFrenchLanguageCode(languageCode = "") {
  return String(languageCode || "").trim().toLowerCase().startsWith("fr");
}

function getLanguageDisplayLabel(value, languageCode) {
  const raw = String(value || "").trim();
  if (!raw || !isFrenchLanguageCode(languageCode)) return raw;
  return FRENCH_LANGUAGE_LABELS[raw] || raw;
}

function hasFrenchLanguageLabel(value) {
  const raw = String(value || "").trim();
  return Boolean(raw && FRENCH_LANGUAGE_LABELS[raw]);
}

function orderLanguageList(items, preferred) {
  const preferredClean = String(preferred || "").trim();
  const values = [
    ...(preferredClean ? [preferredClean] : []),
    ...(Array.isArray(items) ? items : []),
  ];
  const dedup = Array.from(
    new Map(
      values
        .map((n) => String(n || "").trim())
        .filter(Boolean)
        .map((n) => [n.toLowerCase(), n])
    ).values()
  );
  return preferredClean
    ? [
        preferredClean,
        ...dedup.filter((n) => n.toLowerCase() !== preferredClean.toLowerCase()),
      ]
    : dedup;
}

function languageSetKey(items) {
  return JSON.stringify(
    Array.from(
      new Set(
        (Array.isArray(items) ? items : [])
          .map((item) => String(item || "").trim().toLowerCase())
          .filter(Boolean)
      )
    ).sort()
  );
}

export default function PatientLanguages() {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();

  // ─────────────────────────────────────────────
  // Seed from preview (later: DB when we hook it)
  // ─────────────────────────────────────────────
  const preview = React.useMemo(() => loadPatientIntroPreview() || {}, []);

  const initialSelected = React.useMemo(() => {
    if (Array.isArray(preview.languages)) {
      return preview.languages
        .map((item) =>
          typeof item === "string"
            ? item
            : item.language || item.label || item.name || ""
        )
        .filter(Boolean);
    }
    if (typeof preview.languages === "string") {
      return preview.languages
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }, [preview]);

  const initialKey = React.useMemo(
    () => JSON.stringify([...initialSelected].sort()),
    [initialSelected],
  );

  const [all, setAll] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [seedSelected, setSeedSelected] = React.useState(initialSelected);
  const [localSelected, setLocalSelected] = React.useState(initialSelected);
  const [seedPreferred, setSeedPreferred] = React.useState(
    String(preview.primaryLanguage || preview.primary_language || initialSelected[0] || "").trim()
  );
  const [preferredLanguage, setPreferredLanguage] = React.useState(
    String(preview.primaryLanguage || preview.primary_language || initialSelected[0] || "").trim()
  );
  const [showConfirmLeave, setShowConfirmLeave] = React.useState(false);

  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  React.useEffect(() => {
    setSeedSelected(initialSelected);
    setLocalSelected(initialSelected);
    const preferred = String(preview.primaryLanguage || preview.primary_language || initialSelected[0] || "").trim();
    setSeedPreferred(preferred);
    setPreferredLanguage(preferred);
  }, [initialKey]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateSavedLanguages() {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (!alive || authErr || !user?.id) return;

      const [{ data: row, error: rowErr }, { data: ehrRow }] = await Promise.all([
        supabase
        .from("patient_profiles")
        .select("languages")
        .eq("user_id", user.id)
        .maybeSingle(),
        supabase
          .from("ehr_patient_info")
          .select("primary_language")
          .eq("patient_id", user.id)
          .maybeSingle(),
      ]);
      if (!alive || rowErr) return;

      const parsed = Array.isArray(row?.languages)
        ? row.languages
        : String(row?.languages || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

      const deduped = Array.from(
        new Map(parsed.map((item) => [String(item).toLowerCase(), String(item)])).values()
      );
      const preferred = String(ehrRow?.primary_language || deduped[0] || "").trim();
      if (!deduped.length && !preferred) return;

      const ordered = orderLanguageList(deduped, preferred);
      setSeedSelected(ordered);
      setLocalSelected(ordered);
      setSeedPreferred(preferred);
      setPreferredLanguage(preferred);
      savePatientIntroPreview({
        ...(loadPatientIntroPreview() || {}),
        languages: ordered,
        primaryLanguage: preferred,
      });
    }

    hydrateSavedLanguages();
    return () => {
      alive = false;
    };
  }, []);

  // ─────────────────────────────────────────────
  // Load languages from CSVs once
  // ─────────────────────────────────────────────
  React.useEffect(() => {
    let mounted = true;

    (async () => {
      const names = await loadLanguagesFromCSVs(CSV_PATHS);

      const dedup = Array.from(
        new Map(
          names.map((n) => [n.trim().toLowerCase(), n.trim()])
        ).values(),
      );

      const seedOrder = ["English", "Setswana", "Portuguese", "French", "German"];
      const seeded = [
        ...dedup.filter((n) => seedOrder.includes(n)),
        ...dedup
          .filter((n) => !seedOrder.includes(n))
          .sort((a, b) => a.localeCompare(b)),
      ];

      if (mounted) setAll(seeded);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ─────────────────────────────────────────────
  // Filtering + dirty state
  // ─────────────────────────────────────────────
  const q = query.trim().toLowerCase();
  const filtered = q
    ? all.filter((n) => getLanguageDisplayLabel(n, languageCode).toLowerCase().includes(q))
    : all;
  const hideCatalog = shouldHideUntranslatedCatalog(languageCode) || isFrenchLanguageCode(languageCode);
  const visibleOptions = hideCatalog ? [] : filtered;
  const preferredOptions = React.useMemo(() => {
    const options = Array.from(
      new Map(
        [preferredLanguage, ...all]
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .map((item) => [item.toLowerCase(), item])
      ).values()
    );
    if (!isFrenchLanguageCode(languageCode)) return options;
    return options.filter((item) => {
      const key = String(item || "").trim().toLowerCase();
      return (
        key === String(preferredLanguage || "").trim().toLowerCase() ||
        localSelected.some((selected) => String(selected || "").trim().toLowerCase() === key) ||
        hasFrenchLanguageLabel(item)
      );
    });
  }, [all, languageCode, localSelected, preferredLanguage]);
  const preferredSelectOptions = React.useMemo(
    () => preferredOptions.map((name) => ({ value: name, label: getLanguageDisplayLabel(name, languageCode) })),
    [languageCode, preferredOptions]
  );

  const seedKey = React.useMemo(() => languageSetKey(seedSelected), [seedSelected]);
  const sortedLocalKey = React.useMemo(() => languageSetKey(localSelected), [localSelected]);

  const dirty = sortedLocalKey !== seedKey;
  const preferredDirty =
    String(preferredLanguage || "").trim().toLowerCase() !==
    String(seedPreferred || "").trim().toLowerCase();
  const canSave = (dirty || preferredDirty) && localSelected.length > 0 && !!preferredLanguage;

  // ─────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────
  const orderWithPreferred = React.useCallback((next, preferred = preferredLanguage) => {
    const preferredClean = String(preferred || "").trim();
    const values = [
      ...(preferredClean ? [preferredClean] : []),
      ...(Array.isArray(next) ? next : []),
    ];
    const dedup = Array.from(
      new Map(
        values
          .map((n) => String(n || "").trim())
          .filter(Boolean)
          .map((n) => [n.toLowerCase(), n])
      ).values(),
    );
    return preferredClean
      ? [
          preferredClean,
          ...dedup.filter((n) => n.toLowerCase() !== preferredClean.toLowerCase()),
        ]
      : dedup;
  }, [preferredLanguage]);

  const commit = (next) => {
    const dedup = orderWithPreferred(next);
    setLocalSelected(dedup);
  };
  const choosePreferred = (name) => {
    const preferred = String(name || "").trim();
    setPreferredLanguage(preferred);
    setLocalSelected((prev) => orderWithPreferred(prev, preferred));
  };

  const toggle = (name) => {
    setLocalSelected((prev) => {
      const exists = prev.includes(name);
      const isPreferred = String(name || "").trim().toLowerCase() === preferredLanguage.toLowerCase();
      const next = exists
        ? isPreferred
          ? prev
          : prev.filter((x) => x !== name)
        : [...prev, name];
      return orderWithPreferred(next);
    });
  };

  const addCustom = () => {
    const raw = query.trim();
    if (!raw) return;

    const pretty = raw.replace(/\s+/g, " ").trim();

    if (!all.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
      setAll((prev) => [pretty, ...prev]);
    }

    if (!localSelected.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
      commit([pretty, ...localSelected]);
    }

    setQuery("");
  };

  const handleAddClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addCustom();
  };

  // ─────────────────────────────────────────────
  // Save + navigation
  // ─────────────────────────────────────────────
  async function handleSave() {
    if (!canSave) return;

    try {
      const current = loadPatientIntroPreview() || {};
      savePatientIntroPreview({
        ...current,
        languages: orderWithPreferred(localSelected).slice(),
        primaryLanguage: preferredLanguage,
      });

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (!authErr && user?.id) {
        const { error: upErr } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              languages: orderWithPreferred(localSelected).slice(),
            },
            { onConflict: "user_id" }
          );

        if (upErr) throw upErr;
        const { error: ehrErr } = await supabase
          .from("ehr_patient_info")
          .upsert(
            {
              patient_id: user.id,
              primary_language: preferredLanguage,
            },
            { onConflict: "patient_id" }
          );
        if (ehrErr) throw ehrErr;

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      const savedLanguages = orderWithPreferred(localSelected);
      setSeedSelected(savedLanguages);
      setSeedPreferred(preferredLanguage);
      setLocalSelected(savedLanguages);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (err) {
      console.error("[PatientLanguages] Save error:", err);
      setSaveError(
        t(
          "settings.subflows.languages.saveError",
          isFrenchLanguageCode(languageCode)
            ? "Impossible d’enregistrer les modifications. Veuillez réessayer."
            : "Could not save changes. Please try again."
        )
      );
    }
  }

  function handleClose() {
    if (!dirty && !preferredDirty) {
      nav(PATIENT_SETTINGS_PROFILE_ROUTE);
    } else {
      setShowConfirmLeave(true);
    }
  }

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.subflows.languages.title", isFrenchLanguageCode(languageCode) ? "Ajouter des langues" : "Add languages")}
        close
        onBack={handleClose}
        right={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <div className="mt-3">
        <FolaOnboardingCard role="patient" />
      </div>

      {/* Search row */}
      <div className="mt-3 flex items-center gap-3">
        <SearchIcon
          className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
          aria-hidden
        />

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={t("settings.subflows.languages.searchPlaceholder", isFrenchLanguageCode(languageCode) ? "Rechercher ou ajouter" : "Search or add")}
          inputMode="search"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          className="flex-1 bg-transparent outline-none border-0 focus:outline-none focus:ring-0
                     text-[16px] leading-6 placeholder:text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
        />

        {!!q && (
          <TapButton
            ariaLabel={t("settings.subflows.languages.clearSearch", isFrenchLanguageCode(languageCode) ? "Effacer la recherche" : "Clear search")}
            onTap={() => setQuery("")}
            className="shrink-0 grid place-items-center"
          >
            <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
          </TapButton>
        )}

        <button
          type="button"
          onMouseDown={handleAddClick}
          onTouchStart={handleAddClick}
          onClick={handleAddClick}
          className="shrink-0 grid place-items-center rounded-full
                     w-8 h-8 border border-[rgba(24,50,119,0.16)]
                     hover:bg-[rgba(24,50,119,0.04)]
                     active:bg-[rgba(24,50,119,0.08)]
                     focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
          aria-label={t("settings.subflows.languages.addAria", isFrenchLanguageCode(languageCode) ? "Ajouter une langue" : "Add language")}
        >
          <Plus className="h-4 w-4 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
        </button>
      </div>

      <Divider variant="outline" full className="mt-4 mb-6" />

      <div className="mb-6">
        <Select
          id="patient-preferred-language"
          label={t("settings.subflows.languages.preferredLabel", isFrenchLanguageCode(languageCode) ? "Langue préférée / principale" : "Preferred / primary language")}
          value={preferredLanguage}
          onValueChange={choosePreferred}
          placeholder={t("settings.subflows.languages.preferredPlaceholder", isFrenchLanguageCode(languageCode) ? "Sélectionner la langue préférée" : "Select preferred language")}
          options={preferredSelectOptions}
          className="space-y-0"
          triggerClassName="bg-[rgb(var(--clr-surface-cont-highest))] shadow-none text-body-large px-4 pr-5"
          description={t(
            "settings.subflows.languages.preferredHelper",
            isFrenchLanguageCode(languageCode)
              ? "Indiquez la langue que vous préférez pour les soins et la communication."
              : "Tell us the language you prefer for care and communication."
          )}
        />
      </div>

      <p className="text-body-medium text-on-surface-var mb-3">
        {t("settings.subflows.languages.otherLanguages", isFrenchLanguageCode(languageCode) ? "Autres langues parlées" : "Other languages you speak")}
      </p>

      {/* List card */}
      <Card className="relative z-10 overflow-hidden">
        {visibleOptions.map((name, idx) => {
          const checked = localSelected.includes(name);
          return (
            <div key={`${name}-${idx}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggle(name)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggle(name)
                }
                className="w-full flex items-center"
                style={{
                  WebkitTapHighlightColor: "transparent",
                  touchAction: "manipulation",
                }}
              >
                <div className="flex-1 pl-5 pr-4 py-4 text-left">
                  <span className="text-body-large text-[rgb(var(--clr-on-surface,4,8,18))]">
                    {getLanguageDisplayLabel(name, languageCode)}
                  </span>
                </div>
                <div className="mr-4">
                  <Checkbox
                    checked={checked}
                    onChange={(next) => {
                      if (next !== checked) toggle(name);
                    }}
                  />
                </div>
              </div>
              {idx < visibleOptions.length - 1 && (
                <Divider inset variant="outlineVariant" />
              )}
            </div>
          );
        })}
        {visibleOptions.length === 0 && (
          <div className="px-5 py-6 text-body-medium text-on-surface-var">
            {hideCatalog && !q
              ? t(
                  "settings.subflows.languages.catalogHidden",
                  isFrenchLanguageCode(languageCode)
                    ? "Saisissez une langue et appuyez sur + pour l’ajouter. Les suggestions non localisées sont masquées dans cette langue."
                    : "Type a language and tap + to add it. Untranslated suggestions are hidden on this screen in this language.",
                )
              : t(
                  "settings.subflows.languages.noResults",
                  isFrenchLanguageCode(languageCode)
                    ? "Aucune langue ne correspond à votre recherche."
                    : "No languages match your search.",
                )}
          </div>
        )}
      </Card>

      {/* Unsaved-changes confirm */}
      {showConfirmLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowConfirmLeave(false)}
          />
          <div
            className="relative w-[312px] max-w-[560px] min-w-[288px] bg-[var(--Schemes-Surface,#FFFFFF)] rounded-[28px] overflow-hidden
                       shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]"
          >
            <div className="px-6 pt-6">
              <div className="text-headline-small text-on-surface">
                {t(
                  "settings.subflows.discardTitle",
                  isFrenchLanguageCode(languageCode)
                    ? "Abandonner les modifications ?"
                    : "Discard changes?"
                )}
              </div>
              <div className="text-body-medium text-on-surface-var mt-4">
                {t(
                  "settings.subflows.languages.discardBody",
                  isFrenchLanguageCode(languageCode)
                    ? "Vous avez des modifications de langues non enregistrées. Voulez-vous fermer sans enregistrer ?"
                    : "You have unsaved language changes. Are you sure you want to close without saving?"
                )}
              </div>
            </div>

            <div className="p-4 flex gap-3">
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  color: "rgb(var(--afrimed-primary))",
                }}
                onClick={() => setShowConfirmLeave(false)}
              >
                {t("common.keepEditing", isFrenchLanguageCode(languageCode) ? "Continuer la modification" : "Keep editing")}
              </button>
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-Critical,#F8533A) 12%, transparent)",
                  color: "var(--Schemes-Critical,#F8533A)",
                }}
                onClick={() => {
                  setShowConfirmLeave(false);
                  nav(PATIENT_SETTINGS_PROFILE_ROUTE);
                }}
              >
                {t("common.discard", isFrenchLanguageCode(languageCode) ? "Abandonner" : "Discard")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbars */}
      <Snackbar open={saved} onClose={() => setSaved(false)} duration={2200}>
        {t("common.changesSaved", isFrenchLanguageCode(languageCode) ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>

      {saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError}
        </Snackbar>
      )}
    </SettingsScreenShell>
  );
}
